import {
  type ApolloLink,
  type MutationFetchPolicy,
  type OperationVariables,
  type TypedDocumentNode,
} from '@apollo/client';

import { Apollo, type ApolloBase, gql } from 'apollo-angular';

import { type Observable, of } from 'rxjs';
import { map, tap } from 'rxjs/operators';

import { inject } from '@angular/core';
import { ENVIRONMENT_SETTINGS } from '@reconcept-platform/env';
import { getOperationAST, print } from 'graphql';
import { ApolloGraphqlErrorService } from '../apollo/apollo-graphql-error.service';
import '../apollo/apollo-types';
import { throwErrorOrCompleteData } from './graphql.utils';

export type RpfMutationResult = ApolloLink.Result<RpfMutation>;

export interface RpfMutation {
  // we could complete this with all possible mutations for stronger typing
  [mutationName: string]: any;
}

export interface BatchMutationOptions {
  mutations: string[];
  variables: { [variableKey: string]: BatchMutationVariables }[];
  refetchQueries?: any;
  updates?: any[];
  fragments?: TypedDocumentNode<unknown, unknown>[];
  extraOptions?: {
    fetchPolicy?: MutationFetchPolicy;
    awaitRefetchQueries?: boolean;
  };
}

export interface BatchMutationVariables {
  type: string; // the graphQl type
  value: any; // the actual variable value
}

export abstract class GraphqlService {
  protected apolloErrors = inject(ApolloGraphqlErrorService);

  private apolloGraphQLService = inject(Apollo);
  private readonly apollo: ApolloBase;

  protected constructor() {
    const clientName = inject(ENVIRONMENT_SETTINGS).portfolioClientName;
    this.apollo = this.apolloGraphQLService.use(clientName);
  }

  evictFromCacheByIds(type: string, ids: string[]): void {
    const cache = this.getApolloBase().client.cache;
    ids.forEach((id) => {
      cache.evict({
        id: `${type}:${id}`,
      });
    });
    cache.gc();
  }

  /**
   * a clearStore will NOT retrigger watched queries
   *  use for logout
   */
  async clearCache() {
    this.getApolloBase().client.stop(); // needed to stop any in flight queries
    await this.getApolloBase().client.clearStore();
  }

  /**
   * a reset will retrigger watched queries
   *  use for login
   */
  async resetCache() {
    await this.getApolloBase().client.resetStore();
  }

  evictFromRootByFieldName(fieldName: string): void {
    const cache = this.getApolloBase().client.cache;
    cache.evict({
      fieldName,
    });
    cache.gc();
  }

  evictFieldNameFromObject(fieldName: string, id: string, type: string): void {
    const cache = this.getApolloBase().client.cache;
    cache.evict({
      id: `${type}:${id}`,
      fieldName,
    });
    cache.gc();
  }

  // @ts-expect-error TS2344 type constraint not satisfied
  query<QueryType, QueryVariables = OperationVariables>(options: Apollo.WatchQueryOptions<QueryType, QueryVariables>) {
    // @ts-expect-error TS2345 argument not assignable
    return this.getApolloBase().watchQuery(options);
  }

  // @ts-expect-error TS2344 type constraint not satisfied
  watchQuery<QueryType, QueryVariables>(options: Apollo.WatchQueryOptions<QueryType, QueryVariables>) {
    // @ts-expect-error TS2345 argument not assignable
    return this.getApolloBase().watchQuery(options).valueChanges.pipe(throwErrorOrCompleteData());
  }

  queryNetwork<QueryType, QueryVariables = OperationVariables>(
    // @ts-expect-error TS2344 type constraint not satisfied
    options: Apollo.WatchQueryOptions<QueryType, QueryVariables>,
  ) {
    options.fetchPolicy = 'network-only';
    options.nextFetchPolicy = 'cache-first'; // workaround for https://github.com/apollographql/apollo-client/issues/7068

    return this.watchQuery(options);
  }

  /**
   * @deprecated This function does not use type generics to infer return types. Use `mutateV2` instead.
   */
  mutate(options: Apollo.MutateOptions<RpfMutation>): Observable<RpfMutationResult> {
    return this.getApolloBase()
      .mutate<RpfMutation>(options)
      .pipe(map((result) => result as RpfMutationResult));
  }

  watchSubscription<T, V extends OperationVariables>(options: Apollo.SubscribeOptions<T, V>) {
    return this.getApolloBase()
      .subscribe<T, V>(options)
      .pipe(
        // Apollo emits a failure as a result without data, and the error link only wraps the http links
        tap((result) => {
          if (result.error) {
            this.apolloErrors.processSubscription(
              result.error,
              getOperationAST(options.query)?.name?.value ?? 'subscription',
              options.variables,
            );
          }
        }),
      );
  }

  mutateV2<T, V extends OperationVariables>(options: Apollo.MutateOptions<T, V>): Observable<Apollo.MutateResult<T>> {
    return this.getApolloBase()
      .mutate<T, V>(options)
      .pipe(map((result) => result));
  }

  mutateBatch(
    options: BatchMutationOptions,
    gracefulFail?: boolean,
    indexOffset = 0, // used to give the key indexes a unique number if multiple batches are ran an need to be combined
  ): Observable<RpfMutationResult> {
    if (!options.mutations.length) {
      // @ts-expect-error TS2322 type not assignable
      return of(null);
    }

    // merge the mutations and variables
    const mergedVariables: any = [];
    const mutationArguments: string[] = [];

    if (options.variables && options.variables.length) {
      options.variables.forEach((variables, index) => {
        const mutationId = index + indexOffset;

        // append index after each variableName
        // and change mutation at that index correspondingly
        for (const variableKey in variables) {
          if (Object.prototype.hasOwnProperty.call(variables, variableKey)) {
            // define the new variable key
            const newVariableKey = variableKey + '_' + index;

            // add variable value to merged variables object
            mergedVariables[newVariableKey] = variables[variableKey].value;

            // add variable name and type to mutationArguments
            mutationArguments.push(`$${newVariableKey}: ${variables[variableKey].type}`);

            // make an alias for the mutation and replace the variable name
            options.mutations[index] = `mutation_${mutationId}: ${options.mutations[index].replace(
              '$' + variableKey,
              '$' + newVariableKey,
            )}`;
          }
        }
      });
    }

    return this.getApolloBase()
      .mutate<RpfMutation>({
        mutation: gql`
          mutation batchMutation(${mutationArguments.join(',')}){
              ${options.mutations.join('\n')},
          }
          ${options.fragments?.map((f) => print(f)).join('') ?? ''}
      `,
        variables: mergedVariables,
        update: (cache) => options.updates?.forEach((update) => update(cache)),
        refetchQueries: options.refetchQueries,
        // set errorPolicy to all (means don't throw errors by apollo) so we can process errors per mutation
        errorPolicy: gracefulFail ? 'all' : 'none',
      })
      .pipe(
        map((result) => result as RpfMutationResult),
        tap({
          next: (result) => this.apolloErrors.gracefulFailMutation(result, options.mutations[0], mergedVariables),
          error: (error) => this.apolloErrors.processMutation(error, options.mutations[0], mergedVariables),
        }),
      );
  }

  protected getApolloBase(): ApolloBase {
    return this.apollo;
  }
}
